import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { Role } from "@/types/organization-roles";

interface UpdateOrganizationRoleRequest {
  organizationId: number;
  roleId: number;
  name?: string;
  permissions?: string[];
}

interface UpdateOrganizationRoleResponse {
  role: Role;
}

export const useUpdateOrganizationRole = () => {
  const queryClient = useQueryClient();
  const userToken = useUserToken();

  return useMutation(
    ({ organizationId, roleId, name, permissions }: UpdateOrganizationRoleRequest) =>
      fetch(ENDPOINTS.update_organization_role(organizationId, roleId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          ...(name ? { name } : {}),
          ...(permissions ? { permissions } : {}),
        }),
      }).then(handleResponse) as Promise<UpdateOrganizationRoleResponse>,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["organization-roles"]);
        displayToast("Role updated successfully", ToastType.success);
      },
      onError: (error: unknown) => {
        displayErrorToast(
          typeof error === "string" ? error : "Failed to update role. Please try again.",
        );
      },
    },
  );
};
